<template>
  <div
    class="high-frequency-bonus flex justify-center rounded-s py-025 px-050"
    :class="{ animated: isQuickReveal }"
    data-testid="keno-bonus"
    :style="{
      animationDelay: kenoBonusAnimationDelay,
    }"
  >
    <p v-if="!multiplierMode" class="multiplier caption-bold mr-025">
      {{ multiplierCount }}
    </p>
    <p
      v-else
      class="multiplier caption-bold mr-025"
      :class="{
        'multiplier-enter': multiplierMode === 'enter',
        'multiplier-hidden': multiplierMode === 'hidden',
      }"
    >
      <span>{{ multiplierCount }}</span>
    </p>
    <p class="caption-regular">
      {{ multiplierLabel }}
    </p>
  </div>
</template>

<script lang="ts" setup>
type HighFrequencyGameBonusProps = {
  gameId: string
  isQuickReveal?: boolean
  kenoBonus?: number | string
  kenoBonusAnimationDelay?: string
  multiplierMode?: 'enter' | 'hidden'
}

const { t } = useI18n()
const { getTranslation } = useI18nUtils()

const { gameId, kenoBonus } = defineProps<HighFrequencyGameBonusProps>()

const multiplierLabel = computed(() =>
  getTranslation({
    fallback: t('highfrequencygame.order.details.mygames.item.kenomultiplier'),
    key: `highfrequencygame.order.details.mygames.item.kenomultiplier.${gameId}`,
  }),
)

const multiplierCount = computed(() =>
  getTranslation({
    fallback: t('highfrequencygame.order.details.mygames.item.kenobonus.count', {
      count: kenoBonus,
    }),
    key: `highfrequencygame.order.details.mygames.item.kenobonus.count.${gameId}`,
    values: {
      count: kenoBonus,
    },
  }),
)
</script>

<style lang="scss" scoped>
$reveal-animation-duration: 0.3s;
$reveal-animation-delay: 0.03;

.high-frequency-bonus {
  border: 0.0625rem solid token('color/border/primary/primary');

  &.animated {
    opacity: 0;
    animation: fade-in $reveal-animation-duration forwards;
  }

  .multiplier {
    color: var(--card-multiplier-color, token('color/blue/700'));

    &.multiplier-hidden span {
      opacity: 0;
    }

    &.multiplier-enter {
      overflow: hidden;

      span {
        display: block;
        opacity: 0;
        animation: multiplier-enter 0.3s cubic-bezier(0.25, 0.8, 0.4, 1) forwards;
      }
    }
  }

  @keyframes multiplier-enter {
    from {
      opacity: 0;
      transform: translateY(100%);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fade-in {
    to {
      opacity: 1;
    }
  }
}
</style>
